import React, { useEffect, useState } from 'react';
import { getExchangeStatus } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import '../styles/ExchangeStatusPageNew.css';
import { motion } from 'framer-motion';
import { 
  ArrowRightLeft, 
  Filter, 
  ArrowRight, 
  ArrowUpDown, 
  ArrowUp, 
  ArrowDown,
  Building2,
  Calendar
} from 'lucide-react';

/**
 * ExchangeStatusPage
 *
 * 交換申請状況を表示するページ。
 * ダッシュボードのデザインに合わせて刷新。
 */
function ExchangeStatusPage() {
  const [exchangeData, setExchangeData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // フィルター状態
  const [filterSourceTerm, setFilterSourceTerm] = useState('');
  const [filterTargetTerm, setFilterTargetTerm] = useState('');
  const [filterCurrentInstitution, setFilterCurrentInstitution] = useState('');
  const [filterCandidateInstitution, setFilterCandidateInstitution] = useState('');

  // 並び替え状態
  const [sortKey, setSortKey] = useState('source_term');
  const [sortDirection, setSortDirection] = useState('asc');

  const fixedTermOptions = ['1', '2', '3', '4', '5', '6', '7'];
  const [currentInstitutionOptions, setCurrentInstitutionOptions] = useState([]);
  const [candidateInstitutionOptions, setCandidateInstitutionOptions] = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await getExchangeStatus();
        setExchangeData(data);
        setCurrentInstitutionOptions([
          ...new Set(data.map(item => item.current_institution_name).filter(Boolean))
        ]);
        setCandidateInstitutionOptions([
          ...new Set(data.map(item => item.candidate_institution_name).filter(Boolean))
        ]);
      } catch (err) {
        console.error('Error fetching exchange status:', err);
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  // フィルター処理
  const filteredData = exchangeData.filter(item => {
    const matchSourceTerm =
      filterSourceTerm === '' || String(item.source_term) === filterSourceTerm;
    const matchTargetTerm =
      filterTargetTerm === '' || String(item.target_term) === filterTargetTerm;
    const matchCurrentInstitution =
      filterCurrentInstitution === '' ||
      (item.current_institution_name && item.current_institution_name === filterCurrentInstitution);
    const matchCandidateInstitution =
      filterCandidateInstitution === '' ||
      (item.candidate_institution_name && item.candidate_institution_name === filterCandidateInstitution);
    return matchSourceTerm && matchTargetTerm && matchCurrentInstitution && matchCandidateInstitution;
  });

  // 並び替え処理
  const sortedData = [...filteredData].sort((a, b) => {
    let aValue = a[sortKey], bValue = b[sortKey];
    if (sortKey === 'source_term' || sortKey === 'target_term') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    } else {
      aValue = aValue ? aValue.toLowerCase() : '';
      bValue = bValue ? bValue.toLowerCase() : '';
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // ヘッダークリック時のソート処理
  const handleSort = (key) => {
    if (sortKey === key) {
      // 同じカラムの場合、並び順を反転
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // 新たなカラムの場合、昇順に設定
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key) return <ArrowUpDown size={14} className="sort-icon" />;
    return sortDirection === 'asc' 
      ? <ArrowUp size={14} className="sort-icon text-blue-600" /> 
      : <ArrowDown size={14} className="sort-icon text-blue-600" />;
  };

  if (isLoading) {
    return <LoadingSpinner text="データを読み込んでいます..." />;
  }

  if (error) {
    return (
      <div className="exchange-status-page">
        <div className="exchange-container">
          <div className="error-container">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="exchange-status-page">
      <div className="exchange-container">
        {/* ヘッダー */}
        <div className="page-header">
          <h1 className="page-title">
            <ArrowRightLeft className="text-blue-600" size={32} />
            交換申請状況
          </h1>
          <p className="page-description">
            現在提出されている交換申請の一覧を確認できます。
          </p>
        </div>

        {/* フィルターセクション */}
        <motion.section 
          className="filter-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h2 className="filter-title">
            <Filter size={20} />
            フィルター
          </h2>
          <div className="filter-grid">
            <div className="filter-item">
              <label htmlFor="sourceTerm" className="filter-label">現在ターム</label>
              <select 
                id="sourceTerm" 
                className="filter-select"
                value={filterSourceTerm} 
                onChange={e => setFilterSourceTerm(e.target.value)}
              >
                <option value="">全て</option>
                {fixedTermOptions.map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="currentInstitution" className="filter-label">現在病院</label>
              <select 
                id="currentInstitution" 
                className="filter-select"
                value={filterCurrentInstitution} 
                onChange={e => setFilterCurrentInstitution(e.target.value)}
              >
                <option value="">全て</option>
                {currentInstitutionOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="targetTerm" className="filter-label">候補ターム</label>
              <select 
                id="targetTerm" 
                className="filter-select"
                value={filterTargetTerm} 
                onChange={e => setFilterTargetTerm(e.target.value)}
              >
                <option value="">全て</option>
                {fixedTermOptions.map(term => (
                  <option key={term} value={term}>{term}</option>
                ))}
              </select>
            </div>
            <div className="filter-item">
              <label htmlFor="candidateInstitution" className="filter-label">候補病院</label>
              <select 
                id="candidateInstitution" 
                className="filter-select"
                value={filterCandidateInstitution} 
                onChange={e => setFilterCandidateInstitution(e.target.value)}
              >
                <option value="">全て</option>
                {candidateInstitutionOptions.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.section>

        {/* 一覧表示セクション */}
        <motion.section 
          className="table-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          {sortedData.length > 0 ? (
            <div className="table-container">
              <table className="exchange-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('source_term')}>
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        現在ターム
                        {renderSortIcon('source_term')}
                      </div>
                    </th>
                    <th onClick={() => handleSort('current_institution_name')}>
                      <div className="flex items-center gap-1">
                        <Building2 size={16} />
                        現在病院・部署
                        {renderSortIcon('current_institution_name')}
                      </div>
                    </th>
                    <th className="arrow-cell"></th>
                    <th onClick={() => handleSort('target_term')}>
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        希望ターム
                        {renderSortIcon('target_term')}
                      </div>
                    </th>
                    <th onClick={() => handleSort('candidate_institution_name')}>
                      <div className="flex items-center gap-1">
                        <Building2 size={16} />
                        希望病院・部署
                        {renderSortIcon('candidate_institution_name')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map(item => (
                    <tr key={item.preference_id}>
                      <td>
                        <span className="term-badge">{item.source_term}</span>
                      </td>
                      <td>
                        <div className="hospital-info">
                          <span className="hospital-name">{item.current_institution_name}</span>
                          <span className="dept-name">{item.current_department_name}</span>
                        </div>
                      </td>
                      <td className="arrow-cell">
                        <ArrowRight size={20} />
                      </td>
                      <td>
                        <span className="term-badge">{item.target_term || '-'}</span>
                      </td>
                      <td>
                        <div className="hospital-info">
                          <span className="hospital-name">{item.candidate_institution_name || '-'}</span>
                          <span className="dept-name">{item.candidate_department_name || '-'}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">
              <p>該当するデータがありません。</p>
            </div>
          )}
        </motion.section>
      </div>
    </div>
  );
}

export default ExchangeStatusPage;
